/* Continuous muted loops with responsive sources; safe in the theme editor. */
(() => {
  if (!customElements.get('kovali-media')) {
    class KovaliMedia extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.video = this.querySelector('video');
        if (!this.video) return;
        this.ready = true;
        this.abort = new AbortController();
        const listen = (el, type, fn) => el.addEventListener(type, fn, {signal:this.abort.signal});
        this.mobile = matchMedia('(max-width: 749px)');
        this.visible = false;
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.playsInline = true;
        this.video.loop = true;
        this.video.controls = false;
        this.loadSource = () => {
          const src = (this.mobile.matches && this.dataset.mobileSrc) || this.dataset.desktopSrc;
          if (!src || this.activeSource === src) return;
          this.activeSource = src;
          this.video.src = src;
          this.video.load();
        };
        this.reconcile = () => {
          if (this.visible && !document.hidden) {
            this.loadSource();
            if (this.video.paused) this.video.play().catch(() => {});
          } else {
            this.video.pause();
          }
        };
        this.mediaChange = () => { if (this.activeSource) this.loadSource(); this.reconcile(); };
        if (this.mobile.addEventListener) this.mobile.addEventListener('change',this.mediaChange);
        else this.mobile.addListener(this.mediaChange);
        listen(this.video,'error',() => {
          const error = this.querySelector('.kv-media__error');
          if (error) error.hidden = false;
        });
        listen(this.video,'loadeddata',() => {
          const error = this.querySelector('.kv-media__error');
          if (error) error.hidden = true;
          this.reconcile();
        });
        listen(document,'visibilitychange',this.reconcile);
        // A user gesture lets browsers retry muted playback after a policy block.
        listen(document,'pointerdown',this.reconcile);
        listen(document,'keydown',this.reconcile);
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
              this.visible = entry.isIntersecting && entry.intersectionRatio >= .18;
              this.reconcile();
            }
          },{threshold:[0,.18,.5]});
          this.observer.observe(this);
        } else { this.visible = true; this.reconcile(); }
        if (this.dataset.eager === 'true') this.loadSource();
      }
      disconnectedCallback() {
        this.observer?.disconnect();
        this.abort?.abort();
        if (this.mobile?.removeEventListener) this.mobile.removeEventListener('change',this.mediaChange);
        else this.mobile?.removeListener(this.mediaChange);
        this.video?.pause();
        this.ready = false;
      }
    }
    customElements.define('kovali-media',KovaliMedia);
  }
  if (!customElements.get('kovali-video-reviews')) {
    class KovaliVideoReviews extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.track = this.querySelector('[data-video-track]');
        if (!this.track) return;
        this.ready = true;
        this.abort = new AbortController();
        const listen = (el,type,fn) => el.addEventListener(type,fn,{signal:this.abort.signal});
        this.cards = [...this.track.querySelectorAll('.kv-review')];
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.position = this.querySelector('[data-position]');
        this.index = 0;
        this.update = () => {
          const step = this.cards.length > 1 ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : 1;
          this.index = Math.min(this.cards.length - 1,Math.max(0,Math.round(this.track.scrollLeft / step)));
          const max = this.track.scrollWidth - this.track.clientWidth;
          if (max > 0 && this.track.scrollLeft >= max - 3) this.index = this.cards.length - 1;
          this.prev.disabled = this.track.scrollLeft <= 3;
          this.next.disabled = this.track.scrollLeft >= max - 3;
          this.position.textContent = `${this.index + 1} / ${this.cards.length}`;
        };
        this.go = delta => {
          const i = Math.min(this.cards.length - 1,Math.max(0,this.index + delta));
          const step = this.cards.length > 1 ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : 0;
          this.track.scrollTo({left:i * step,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
        };
        listen(this.prev,'click',() => this.go(-1));
        listen(this.next,'click',() => this.go(1));
        listen(this.track,'scroll',() => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(this.update); });
        listen(this.track,'keydown',event => {
          if (event.target !== this.track) return;
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.go(event.key === 'ArrowRight'?1:-1); }
        });
        if ('ResizeObserver' in window) { this.resize = new ResizeObserver(this.update); this.resize.observe(this.track); }
        else listen(window,'resize',this.update);
        this.update();
      }
      disconnectedCallback() {
        this.abort?.abort();this.resize?.disconnect();cancelAnimationFrame(this.frame);this.ready = false;
      }
    }
    customElements.define('kovali-video-reviews',KovaliVideoReviews);
  }
})();
